// Reusable alert/notification component with variant styles and optional dismiss button.
// Full dark mode support from day one.

import { X } from 'lucide-react';
import { clsx } from 'clsx';

interface AlertProps {
  variant: 'error' | 'warning' | 'info' | 'success';
  children: React.ReactNode;
  onDismiss?: () => void;
  className?: string;
  /** Lets a form control point at this message with `aria-describedby`, so a
   *  validation error is announced against the field it belongs to rather
   *  than only as a standalone alert. */
  id?: string;
}

const variantClasses: Record<AlertProps['variant'], string> = {
  error: 'bg-red-50 border-red-200 text-red-700 dark:bg-red-900/20 dark:border-red-800 dark:text-red-400',
  warning: 'bg-amber-50 border-amber-200 text-amber-700 dark:bg-amber-900/20 dark:border-amber-800 dark:text-amber-400',
  info: 'bg-brand-50 border-brand-200 text-brand-700 dark:bg-brand-900/20 dark:border-brand-800 dark:text-brand-400',
  success: 'bg-emerald-50 border-emerald-200 text-emerald-700 dark:bg-emerald-900/20 dark:border-emerald-800 dark:text-emerald-400',
};

export default function Alert({ variant, children, onDismiss, className, id }: AlertProps) {
  return (
    <div
      id={id}
      role="alert"
      className={clsx(
        'flex items-start gap-3 px-4 py-3 rounded-lg border text-body-sm',
        variantClasses[variant],
        className
      )}
    >
      <div className="flex-1">{children}</div>
      {onDismiss && (
        <button
          type="button"
          onClick={onDismiss}
          aria-label="Dismiss"
          className="shrink-0 p-0.5 rounded hover:bg-black/10 dark:hover:bg-white/10 transition-colors"
        >
          <X className="h-4 w-4" />
        </button>
      )}
    </div>
  );
}
